/**
 * Dependencies.
 */
import Moment from 'moment-timezone';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

import TrackingModel from '@/api/tracking/tracking.model';
import { TRACKING_STATUS } from '@/modules/tracking/constants';
import { trackingStore } from '@/modules/tracking/store';

/**
 * Component.
 */
@Component({ name: 'status-processing-component' })
export default class extends Vue {
  /**
   * Property `moment`.
   */
  moment = Moment;

  /**
   * Property `windowHeight`;
   */
  windowHeight = null;

  /**
   * Property `client`.
   */
  @Prop({ type: String })
  client: string;

  /**
   * Property `distributionCenter`.
   */
  @Prop({ type: String })
  distributionCenter: string;

  /**
   * Property `tracking`.
   */
  @Prop({ type: Object })
  tracking: TrackingModel;

  /**
   * Property `supportUrl`.
   */
  @Prop({ default: null })
  supportUrl: string;

  /**
   * Property `orderType`.
   */
  @Prop({ default: null, type: String })
  orderType: string;

  /**
   * Property `numberOfPackages`.
   */
  @Prop({ default: 0, type: Number })
  numberOfPackages: number;

  /**
   * Property `isOrderProcessDelayed`.
   */
  @Prop({ default: false, type: Boolean })
  isOrderProcessDelayed: boolean;

  /**
   * Getter `isDelivery`
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter processing status title.
   */
  get processingStatusTitle() {
    return this.i18n('TRACKING.SENTENCES.STATUS_HELLO', false, 'upperFirst', {
      name: this.client || '-'
    });
  }

  /**
   * Getter processing status sentence.
   */
  get processingStatusSentence() {
    const { statusDate } = this.tracking || {};

    const date = this.moment(statusDate).format('DD MMM YYYY');
    const hour = this.moment(statusDate).format('HH:mm');

    return this.i18n(
      `TRACKING.SENTENCES.STATUS_${
        this.isDelivery ? 'DELIVERY' : 'COLLECT'
      }_PROCESSING`,
      false,
      'plain',
      {
        date: `<b>${date || '-'}</b>`,
        hour: `<b>${hour || '-'}</b>`,
        location: `<b>${this.distributionCenter || '-'}</b>`,
        packages:
          this.numberOfPackages > 1
            ? this.i18n(
              'TRACKING.SENTENCES.NUMBER_OF_PACKAGES',
              false,
              'plain',
              { total: this.numberOfPackages }
            )
            : ''
      }
    );
  }

  /**
   * Getter `extraSmallScreenHeight`.
   */
  get extraSmallScreenHeight() {
    return this.windowHeight <= 320;
  }

  /**
   * Getter `canShowWarning`.
   */
  get canShowWarning() {
    const trackingHistory = trackingStore.tracking || [];
    const hasBeenOnRoute =
      trackingHistory.findIndex(
        tracking => tracking?.status?.code === TRACKING_STATUS.IN_ROUTING
      ) !== -1;

    return this.isOrderProcessDelayed && !hasBeenOnRoute;
  }

  /**
   * Getter `warningSentence`.
   */
  get warningSentence() {
    const support = `<a href="${this.supportUrl}" target="_blank">${this.i18n(
      'TRACKING.MESSAGES.CUSTOMER_SUPPORT',
      false,
      'plain'
    )}</a>`;

    const message = this.i18n(
      'TRACKING.SENTENCES.WARNING_DELIVERY_DELAY',
      false,
      undefined,
      {
        support
      }
    );

    return message;
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    this.handleOnResize();

    this.$nextTick(() => {
      window.addEventListener('resize', this.handleOnResize);
    });
  }

  /**
   * Lifecycle destroyed hook.
   */
  destroyed() {
    window.removeEventListener('resize', this.handleOnResize);
  }

  /**
   * Handle on resize.
   */
  handleOnResize() {
    this.windowHeight = window.innerHeight;
  }

  /**
   * Handle window height.
   */
  @Watch('windowHeight')
  handlewindowHeight(value) {
    this.windowHeight = value;
  }
}
